import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import '../../component-styles/QuickbooksConnectModal.css';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';

// How long to keep listening for the QuickBooks window to finish.
const CONNECT_WATCH_MS = 10 * 60 * 1000;
// Server and browser clocks rarely agree to the second.
const CLOCK_SKEW_MS = 2 * 60 * 1000;

function getDefaultMessage(mode) {
  if (mode === 'invoice') {
    return 'To create invoices and sync billing, you will need to connect QuickBooks to your account.';
  }

  return 'To create estimates and sync proposals, you will need to connect QuickBooks to your account.';
}

// Waits for the QuickBooks window opened by Connect to finish. The callback
// page posts a message on success, but Intuit's sign-in pages can sever its
// link back to this window — so coming back to this tab also re-checks the
// connection. Returns a function that stops watching.
function watchForQuickBooksConnection(onConnected) {
  const startedAt = Date.now();
  const apiOrigin = new URL(API_BASE, window.location.origin).origin;
  let done = false;
  let timeoutId = null;

  const stop = () => {
    done = true;
    clearTimeout(timeoutId);
    window.removeEventListener('message', handleMessage);
    window.removeEventListener('focus', handleFocus);
  };

  const finish = () => {
    if (done) return;
    stop();
    onConnected();
  };

  const handleMessage = (event) => {
    if (event.origin === apiOrigin && event.data?.type === 'quickbooks:connected') finish();
  };

  const handleFocus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/quickbooks/status`);
      const data = await res.json().catch(() => ({}));
      // A connection made before Connect was clicked doesn't count — after an
      // "authorization expired" error the stale one can still read as connected.
      const connectedAt = Date.parse(data?.connectedAt || '');
      if (data?.connected && connectedAt >= startedAt - CLOCK_SKEW_MS) finish();
    } catch {
      // Checked again the next time the tab regains focus.
    }
  };

  timeoutId = setTimeout(stop, CONNECT_WATCH_MS);
  window.addEventListener('message', handleMessage);
  window.addEventListener('focus', handleFocus);
  return stop;
}

export default function QuickBooksConnectModal({
  open,
  onClose,
  // Called once QuickBooks is connected — callers pass the sync that was
  // blocked so it runs again without the user having to click it.
  onConnected,
  title = 'QuickBooks Not Connected',
  message,
  connectLabel = 'Connect QuickBooks',
  mode = 'proposal',
}) {
  const stopWatchingRef = useRef(null);
  // Latest callback, so a retry minutes later sees the page's current state.
  const onConnectedRef = useRef(onConnected);

  useEffect(() => {
    onConnectedRef.current = onConnected;
  }, [onConnected]);

  // Stop listening once the page that asked for the connection goes away.
  useEffect(() => () => stopWatchingRef.current?.(), []);

  if (!open) return null;

  function handleConnect() {
    // The account owner's id, not the signed-in seat's: team members share
    // the owner's QuickBooks connection.
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    window.open(`${API_BASE}/api/quickbooks/connect?userId=${encodeURIComponent(userId)}`, '_blank');

    stopWatchingRef.current?.();
    stopWatchingRef.current = watchForQuickBooksConnection(() => {
      stopWatchingRef.current = null;
      notify.success('QuickBooks connected.');
      onConnectedRef.current?.();
    });
    onClose();
  }

  return (
    <div className="qb-modal-overlay" onClick={onClose}>
      <div className="qb-modal" onClick={(e) => e.stopPropagation()}>
        <button className="qb-modal-close" onClick={onClose}>
          <X size={16} />
        </button>

        <h3 className="qb-modal-title">{title}</h3>

        <p className="qb-modal-text">{message || getDefaultMessage(mode)}</p>

        <div className="qb-modal-actions">
          <button className="if-btn-secondary" onClick={onClose}>
            Cancel
          </button>

          <button className="if-btn-primary" onClick={handleConnect}>
            {connectLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
